import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronRight, Phone, Star, MapPin, CheckCircle2, Trophy, Users, BookOpen, Target } from 'lucide-react';
import { LOGO, WA_NUMBER, PHONE, INSTAGRAM_URL, GOOGLE_MAPS_URL, GOOGLE_REVIEWS_COUNT } from '@/pages/LandingPage';

const PAGES = {
  '/psc-coaching-varkala': {
    h1: 'Best PSC Coaching in Varkala — Signature PSC Coaching Centre',
    title: 'Best PSC Coaching in Varkala | Signature PSC Coaching Centre, Palachira',
    description: 'Looking for the best PSC coaching in Varkala? Signature PSC Coaching Centre, Palachira offers 10+ years of expert Kerala PSC preparation, daily current affairs, mock tests, expert mentorship. 172+ Google Reviews. Call 8281583404.',
    intro: 'Signature PSC Coaching Centre, Palachira, Varkala is widely regarded as the best PSC coaching centre in Varkala, delivering 10+ years of proven results across LDC, LGS, Company Board Assistant, Uniform Services, Devaswom Board and Degree Level Kerala PSC examinations. Our students consistently achieve top ranks because of our structured curriculum, daily current affairs sessions, expert faculty, and personalised mentorship — everything a serious Kerala PSC aspirant needs under one roof.',
    bullets: [
      'Specialised Kerala PSC coaching across all exam categories — LDC, LGS, Degree Level, Uniform Services',
      'Daily current affairs and weekly mock tests as part of the regular programme',
      'Dedicated classrooms with limited seats per batch for personal attention',
      'Free mentorship, printed notes and exam-pattern question banks included',
      'Flexible batch timings — Regular, Evening, Holiday and Free batch options',
      'Trusted by 500+ Varkala aspirants — 172+ verified Google Reviews',
    ],
    cta: 'Join Signature PSC — Varkala\'s most trusted PSC coaching centre',
  },
  '/ldc-coaching-varkala': {
    h1: 'LDC Coaching in Varkala — Crack Kerala PSC LDC with Confidence',
    title: 'Best LDC Coaching Varkala | Kerala PSC LDC Preparation | Signature PSC',
    description: 'Specialised LDC coaching in Varkala. Our Kerala PSC LDC programme covers full syllabus, weekly mock tests, daily current affairs, previous papers and personalised mentorship. Call 8281583404 to enrol.',
    intro: 'Our LDC coaching in Varkala is built specifically around the Kerala PSC LDC syllabus and exam pattern. The programme covers General Knowledge, English, Maths, Mental Ability, Kerala-specific GK and daily current affairs — with weekly mock tests and previous-paper analysis sessions. Whether you are a fresh aspirant or a repeater preparing for the next LDC exam, Signature PSC gives you a clear, structured path from foundation to final mock.',
    bullets: [
      'Complete Kerala PSC LDC syllabus covered with topic-wise notes',
      'Weekly LDC-pattern mock tests with rank list and detailed solutions',
      'Daily current affairs from Kerala-relevant news sources',
      'Maths & Mental Ability shortcut techniques and speed practice',
      'Special doubt-clearing classes and one-on-one mentor sessions',
      'Affordable monthly fees starting from ₹800 — no hidden costs',
    ],
    cta: 'Start your LDC preparation today',
  },
  '/kerala-psc-current-affairs': {
    h1: 'Kerala PSC Current Affairs — Daily Updates for Aspirants',
    title: 'Kerala PSC Current Affairs Daily | PSC Notifications | Signature PSC',
    description: 'Daily Kerala PSC current affairs, notifications, mock questions and short video lessons — curated for serious Kerala PSC aspirants by Signature PSC Coaching Centre Varkala. Call 8281583404.',
    intro: 'Current affairs is the highest-scoring section of every Kerala PSC examination — yet most aspirants struggle to consume it consistently. At Signature PSC Coaching Centre, we publish daily Kerala PSC current affairs, official notifications, mock questions and short video lessons so that you can prepare anywhere, any time. Our content is curated by experienced faculty and aligned to the Kerala PSC exam pattern.',
    bullets: [
      'Daily current affairs in concise, exam-ready format',
      'Latest Kerala PSC notifications with application deadlines',
      'Topic-wise mock questions with detailed explanations',
      'Short video lessons (2-5 minutes) on tricky concepts',
      'WhatsApp updates — never miss an important notification',
      'Free preview content; full programme available to enrolled students',
    ],
    cta: 'Get daily Kerala PSC updates on WhatsApp',
  },
};

export default function SeoLanding() {
  const { pathname } = useLocation();
  const data = PAGES[pathname] || PAGES['/psc-coaching-varkala'];

  useEffect(() => {
    document.title = data.title;
    let meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', data.description);
    let can = document.querySelector('link[rel="canonical"]');
    if (can) can.setAttribute('href', `https://signaturepsc.com${pathname}`);
  }, [pathname, data]);

  return (
    <div className="min-h-screen bg-[#0c1a0e]" data-testid={`seo-landing-${pathname.replace(/\//g, '')}`}>
      {/* Mini-nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0c1a0e]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="Signature PSC" className="w-9 h-9 rounded-full object-contain bg-white p-0.5" />
            <div>
              <span className="text-sm font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>SIGNATURE</span>
              <span className="text-[10px] block text-[#c8b87a] tracking-[0.15em] uppercase -mt-0.5">PSC Coaching Centre</span>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-4">
            <a href={`tel:+91${PHONE}`} className="flex items-center gap-1.5 text-sm text-[#c8b87a]"><Phone className="w-4 h-4" /> {PHONE}</a>
            <Link to="/login"><Button className="bg-gradient-to-r from-[#c8a84e] to-[#a07c2a] text-white rounded-full">Login</Button></Link>
          </div>
        </div>
      </nav>

      <header className="pt-32 pb-12 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="flex items-center gap-2 text-xs text-white/40 mb-4">
          <Link to="/" className="hover:text-[#c8b87a]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#c8b87a]">{pathname.replace('/', '').replace(/-/g, ' ')}</span>
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5">
            <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-[#fbbf24] text-[#fbbf24]" />)}</div>
            <span className="text-xs text-white font-semibold">{GOOGLE_REVIEWS_COUNT} Google Reviews</span>
          </a>
          <span className="inline-flex items-center gap-1 text-xs text-white/60"><MapPin className="w-3.5 h-3.5" /> Palachira, Varkala, Kerala</span>
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>{data.h1}</h1>
        <p className="text-white/65 leading-relaxed mt-6 max-w-3xl">{data.intro}</p>
        <div className="flex flex-wrap gap-3 mt-8">
          <a href={`tel:+91${PHONE}`}>
            <Button className="bg-gradient-to-r from-[#c8a84e] to-[#a07c2a] text-white font-semibold px-6 rounded-full"><Phone className="w-4 h-4 mr-2" /> Call {PHONE}</Button>
          </a>
          <a href={`https://wa.me/${WA_NUMBER}?text=Hi, I'm interested via your ${pathname} page.`} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#25D366] text-white font-semibold px-6 rounded-full">WhatsApp Now</Button>
          </a>
        </div>
      </header>

      <section className="py-12 px-6 md:px-12 max-w-7xl mx-auto">
        <h2 className="text-xl sm:text-2xl font-semibold text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Why aspirants choose us</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {data.bullets.map((b, i) => (
            <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-[#142318] border border-white/10">
              <CheckCircle2 className="w-5 h-5 text-[#c8a84e] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-white/80 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-12 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { icon: Users, label: '500+ Students Trained' },
            { icon: Trophy, label: '172+ Google Reviews' },
            { icon: BookOpen, label: 'Expert Faculty' },
            { icon: Target, label: 'Daily Mock Tests' },
          ].map((it, i) => (
            <div key={i} className="p-5 rounded-xl bg-[#142318] border border-white/10 text-center">
              <it.icon className="w-6 h-6 text-[#c8a84e] mx-auto mb-2" />
              <p className="text-sm text-white/80">{it.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 px-6 md:px-12 max-w-7xl mx-auto text-center">
        <h2 className="text-2xl sm:text-3xl font-semibold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{data.cta}</h2>
        <p className="text-white/55 mb-8">Call us today to book a free counselling session</p>
        <div className="flex flex-wrap justify-center gap-3">
          <a href={`tel:+91${PHONE}`}>
            <Button className="bg-gradient-to-r from-[#c8a84e] to-[#a07c2a] text-white font-semibold px-7 py-3 rounded-full text-base"><Phone className="w-4 h-4 mr-2" /> Call {PHONE}</Button>
          </a>
          <Link to="/"><Button variant="outline" className="border-white/20 text-white hover:bg-white/10 bg-transparent rounded-full px-7 py-3 text-base">Explore Full Website</Button></Link>
        </div>
        <div className="mt-6 text-xs text-white/40">
          Related: <Link to="/psc-coaching-varkala" className="text-[#c8b87a] hover:underline mx-2">PSC Coaching Varkala</Link>·
          <Link to="/ldc-coaching-varkala" className="text-[#c8b87a] hover:underline mx-2">LDC Coaching Varkala</Link>·
          <Link to="/kerala-psc-current-affairs" className="text-[#c8b87a] hover:underline mx-2">Kerala PSC Current Affairs</Link>
        </div>
      </section>

      <footer className="py-8 border-t border-white/10 text-center">
        <p className="text-xs text-white/30">&copy; {new Date().getFullYear()} SIGNATURE PSC COACHING CENTRE, Palachira, Varkala</p>
      </footer>
    </div>
  );
}
